import { cn } from "nazare-ui/utils";
import styles from "./styles/scroll-shadow.module.css";

/**
 * Rendered height of this app's shared sticky `<thead>` row — every table
 * built on `styles/maximised-table.module.css`'s shared `.th`/`.td` (or a
 * page-local duplicate of it) uses the same explicit `height: 58px`, so one
 * constant covers all of them rather than each file guessing its own. Keep
 * this in sync with that CSS value — it was previously 34 (the header's old,
 * shorter rendered height before `.th` was made to match `.td`'s real
 * content height), which left the shadow floating under the header instead
 * of below it once the header grew. Pass as
 * `<ScrollShadow topOffset={TABLE_HEADER_HEIGHT}>` for any scrollable table
 * with a sticky header; omit for a header-less scrollable list (e.g. the
 * dashboard summary cards).
 */
export const TABLE_HEADER_HEIGHT = 58;

export type ScrollShadowProps = {
  atTop: boolean;
  atBottom: boolean;
  /**
   * Pixels to push the TOP shadow down by — for a scrollable table with a
   * sticky `<thead>` row, this should be that row's rendered height, so the
   * shadow sits in the actual row-scrolling area right below the header
   * instead of overlapping the header itself. Without this, the shadow's
   * `top: 0` coincides with the header, and stacking it BEHIND the header
   * (z-index) makes it invisible rather than "under" in the intended,
   * vertically-below sense. Omit (default 0) for a scrollable list with no
   * header, e.g. the dashboard summary cards.
   */
  topOffset?: number;
  /**
   * Overrides the shadow's default `z-index: 0` (see
   * `scroll-shadow.module.css`). Needed when the scrollable content itself
   * has `position: sticky` cells with their OWN z-index above 0 — e.g. the
   * notifications matrix's pinned checkbox/user columns (`z-index: 1`/`2`)
   * — which otherwise paint over the shadow and hide it wherever it would
   * have overlapped them, even though that pinned column is genuinely
   * scrolling content too. Pass a value higher than every sticky BODY cell
   * it needs to cover, but lower than the sticky `<thead>` itself (so the
   * shadow still sits visually below the header, not on top of it). Omit
   * for the common case (no sticky body columns) — default 0 is unchanged.
   */
  zIndex?: number;
};

/**
 * Renders the two curved edge-shadow overlays for a scrollable container —
 * pair with `useScrollEdges()` and `styles.scrollShadowWrapper` (see
 * `styles/scroll-shadow.module.css`'s doc comment for the required DOM
 * structure: this must be a sibling of the actual `overflow-y: auto`
 * element, both inside one `scrollShadowWrapper` div, NOT a descendant of
 * the scrolling element itself).
 */
export function ScrollShadow({ atTop, atBottom, topOffset = 0, zIndex }: ScrollShadowProps) {
  return (
    <>
      <div
        className={cn(styles.shadowTop, atTop && styles.shadowHidden)}
        style={{ ...(topOffset ? { top: topOffset } : undefined), ...(zIndex != null ? { zIndex } : undefined) }}
        aria-hidden="true"
      />
      <div
        className={cn(styles.shadowBottom, atBottom && styles.shadowHidden)}
        style={zIndex != null ? { zIndex } : undefined}
        aria-hidden="true"
      />
    </>
  );
}
